import { CurrencyInput, Input } from '@cimplico/components';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { Controller, useFormContext } from 'react-hook-form';
import CimplicoStaffGuard from '../../../components/cimplico-staff-guard/CimplicoStaffGuard';
import WorkpaperSelect from '../../../components/workpaper-select/WorkpaperSelect';
import { useWorkpaperContext } from '../../../providers/WorkpaperProvider';
import { WorkpaperSourceEnum } from '../../../types/workpaper';

type DetailsProps = {
    isDisabled: boolean;
};

function DetailsSection({ isDisabled }: DetailsProps): JSX.Element {
    const { control, formState, trigger, clearErrors, watch } = useFormContext();
    const { provider, entity, id, priorWorkpaperId } = useWorkpaperContext();

    const periodEnd = watch('periodEnd');

    return (
        <div className="flex flex-col gap-2">
            <h3 className="font-medium text-gray-800 text-lg">Details</h3>
            <div className="flex flex-col gap-8">
                {/* Workpaper Name */}
                <Controller
                    name="name"
                    control={control}
                    rules={{ required: 'A name is required.' }}
                    render={({ field }): JSX.Element => (
                        <Input
                            label="Workpaper Name"
                            type="text"
                            placeholder="Name for the workpaper..."
                            isError={Boolean(formState.errors.name)}
                            errorMessage={formState.errors.name?.message?.toString()}
                            disabled={isDisabled}
                            onChange={(e): void => {
                                field.onChange(e.target.value);
                                trigger('name');
                            }}
                            value={field.value}
                        />
                    )}
                />
                <div className="flex flex-col gap-2">
                    {/* WORKPAPER PERIOD */}
                    <Controller
                        name="periodEnd"
                        control={control}
                        rules={{ required: 'A period end must be specified.' }}
                        render={({ field: { onChange, value, onBlur } }): JSX.Element => (
                            <Input
                                label="Workpaper Period End Date"
                                type="date"
                                placeholder="Period end date..."
                                isError={Boolean(formState.errors.periodEnd)}
                                errorMessage={formState.errors.periodEnd?.message?.toString()}
                                disabled={isDisabled}
                                onChange={(e): void => {
                                    onChange(e.target.value);
                                    clearErrors('periodEnd');
                                }}
                                onBlur={onBlur}
                                value={value ? new Date(value).toISOString().substring(0, 10) : ''}
                            />
                        )}
                    />
                    {formState.dirtyFields.periodEnd && provider === WorkpaperSourceEnum.XERO && (
                        <div className="flex items-start gap-2 text-sm">
                            <ExclamationTriangleIcon className="text-yellow-600 w-4 h-4 shrink-0" />
                            <p className="text-gray-500 leading-4">
                                Changing this will automatically refresh the trial balance which may affect the status of current
                                worksheets.
                            </p>
                        </div>
                    )}
                </div>

                {/* WORKPAPER DUE DATE */}
                <Controller
                    name="dueDate"
                    control={control}
                    rules={{ required: 'A due date must be specified.' }}
                    render={({ field: { onChange, onBlur, value } }): JSX.Element => (
                        <Input
                            min={periodEnd.substring(0, 10)}
                            label="Workpaper Due Date"
                            type="date"
                            placeholder="Due date..."
                            isError={Boolean(formState.errors.dueDate)}
                            errorMessage={formState.errors.dueDate?.message?.toString()}
                            disabled={isDisabled}
                            onChange={(e): void => {
                                onChange(e.target.value);
                                clearErrors('dueDate');
                            }}
                            onBlur={onBlur}
                            value={value ? new Date(value).toISOString().substring(0, 10) : ''}
                        />
                    )}
                />
                {/* MATERIALITY LIMIT */}
                <Controller
                    name="materialityLimit"
                    control={control}
                    render={({ field }): JSX.Element => (
                        <CurrencyInput
                            label="Materiality Limit"
                            placeholder="Amount for auto-exclusion..."
                            onChange={(e): void => {
                                field.onChange(e.target.value);
                                trigger('materialityLimit');
                            }}
                            disabled={isDisabled}
                            defaultValue={field.value}
                            isOptional
                            textAlign="left"
                            hint="* Worksheets with checklist items won't be excluded."
                        >
                            <Controller
                                name="materialityLimitType"
                                control={control}
                                render={({ field }): JSX.Element => (
                                    <Input.Dropdown
                                        onChange={(e): void => {
                                            field.onChange(e.target.value);
                                            trigger('materialityLimitType');
                                        }}
                                        disabled={isDisabled}
                                        defaultValue={field.value}
                                        name="materialityLimitType"
                                        options={[
                                            { label: '$', value: 'monetary' },
                                            { label: '%', value: 'percentage' },
                                        ]}
                                    />
                                )}
                            />
                        </CurrencyInput>
                    )}
                />
                {/* PRIOR WORKPAPER */}
                <CimplicoStaffGuard>
                    <Controller
                        name="priorWorkpaperId"
                        rules={{ required: 'A previous workpaper is required.' }}
                        control={control}
                        // disabled={Boolean(priorWorkpaperId)}
                        render={({ field: { onChange, ref, value } }): JSX.Element => {
                            return (
                                <div>
                                    <WorkpaperSelect
                                        disabled={Boolean(priorWorkpaperId)}
                                        label="Prior Workpaper"
                                        entity={entity}
                                        onChange={(selectedId) => {
                                            clearErrors('priorWorkpaperId');
                                            onChange(selectedId);
                                        }}
                                        currentPeriodEnd={periodEnd}
                                        value={value}
                                        innerRef={ref}
                                        placeholder="Select a prior workpaper..."
                                        disabledId={id}
                                    />
                                    <p className="mt-2 text-sm text-gray-500">
                                        *Resources can be carried forward from this selected workpaper
                                    </p>
                                </div>
                            );
                        }}
                    />
                </CimplicoStaffGuard>
            </div>
        </div>
    );
}

export default DetailsSection;
